import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import QuestionNavPanel from "../../../components/ExamTaking/QuestionNavPanel";
import QuestionDisplay from "../../../components/ExamTaking/QuestionDisplay";
import PassagePane from "../../../components/ExamTaking/PassagePane";
import Calculator from "../../../components/ExamTaking/Calculator";
import PeriodicTable from "../../../components/ExamTaking/PeriodicTable";
import KaTeXComponent from "../../../components/KaTeXComponent/KaTeXComponent";
import { isBlankHtml } from "../../../components/KaTeXComponent/RichText";
import { DIFFICULTY_LEVELS, DifficultyBadge, isDifficulty } from "../../../components/Difficulty/Difficulty";
import { getTests } from "../../../services/Exams/ExamsService";
import { getPassage } from "../../../services/passageService";
import { getApiErrorMessage } from "../../../utils/apiError";
import { isCanceledError } from "../../../utils/examError";
import { passageRuns } from "../../../utils/passages";
import { answersHidden, focusRing, linkButtonClass, Spinner } from "./ui";

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

// The test's rows in the shape of an attempt's questions, so the exam's own components show them.
// Codes stay hidden, as students don't see them; `correct` is kept for "Show correct answers" (none for a question
// whose answers the user may not read: answers_hidden, said on its card instead).
const toAttemptQuestions = (test) =>
  [...(test?.test_questions || [])]
    .sort(byOrder)
    .filter((tq) => tq.question && typeof tq.question === "object")
    .map((tq, i) => ({
      attempt_question_uuid: tq.uuid,
      test_uuid: test.uuid,
      test_name: test.name,
      question_uuid: tq.question.uuid,
      question_code: null,
      question_body: tq.question.body || "",
      points: tq.points,
      order: i + 1,
      options: (tq.question.answer_keys || []).map(({ uuid, option, body }) => ({ uuid, option, body })),
      correct: (tq.question.answer_keys || []).filter((key) => key.is_correct).map((key) => key.uuid),
      answers_hidden: answersHidden(tq.question),
      passage_uuid: tq.question.passage?.uuid || null,
      difficulty: tq.question.difficulty || null,
    }));

// The header's counters: questions, their points in this test (decimal strings) and how many have each level.
const summarize = (questions) => {
  const levels = {};
  let points = 0;
  questions.forEach((q) => {
    points += Number(q.points) || 0;
    if (isDifficulty(q.difficulty)) levels[q.difficulty] = (levels[q.difficulty] || 0) + 1;
  });
  return { count: questions.length, points: Math.round(points * 100) / 100, levels };
};

const navButtonClass = `inline-flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 sm:px-5 ${focusRing}`;

/**
 * The test as a student sees it in the exam: the question list, passages next to their questions, the
 * question cards, the calculator and periodic table when the test allows them. Answers picked here are only kept on this screen (no attempt, no timer, no proctoring);
 * "Show correct answers" marks the key. `prepare` runs first (the builder's pending writes), then the test
 * is loaded as saved. A test already loaded with its full rows (`test`: {name, test_questions: [{uuid, order,
 * points, question}], calculator_is_allowed, ...}, e.g. a subject variant's detail) is shown as it is instead;
 * `badge` and `hint` then say what it is (a test's words by default). `passages` ([{uuid, body}]) are its texts
 * when they came with it (else each is read); `notice` is a line under the header (e.g. what was left out).
 */
export default function TestPreview({ open, testUuid, test: preloaded, passages: givenPassages, prepare, onClose, badge, hint, notice }) {
  const [t] = useTranslation();
  const [state, setState] = useState({ loading: true, error: null, test: null, passages: {} });
  const [attempt, setAttempt] = useState(0);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showKey, setShowKey] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const scrollPositions = useRef(new Map());
  const mainRef = useRef(null);
  const columnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setState({ loading: true, error: null, test: null, passages: {} });
    setIndex(0);
    setAnswers({});
    scrollPositions.current = new Map();
    (async () => {
      try {
        let test = preloaded;
        if (!test) {
          await prepare?.();
          test = await getTests({ params: { uuid: testUuid }, signal: controller.signal });
        }
        const ids = [...new Set((test?.test_questions || []).map((tq) => tq.question?.passage?.uuid).filter(Boolean))];
        const details = Array.isArray(givenPassages)
          ? givenPassages
          : await Promise.all(
              ids.map((uuid) =>
                getPassage(uuid, { signal: controller.signal }).catch((err) => {
                  if (isCanceledError(err)) throw err;
                  return null;
                })
              )
            );
        const passages = {};
        details.filter(Boolean).forEach((detail) => {
          passages[detail.uuid] = { uuid: detail.uuid, body: detail.body };
        });
        if (!controller.signal.aborted) setState({ loading: false, error: null, test, passages });
      } catch (err) {
        if (!isCanceledError(err)) setState({ loading: false, error: err, test: null, passages: {} });
      }
    })();
    return () => controller.abort();
  }, [open, testUuid, preloaded, givenPassages, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const questions = useMemo(() => toAttemptQuestions(state.test), [state.test]);
  const totals = useMemo(() => summarize(questions), [questions]);
  const { runByIndex } = useMemo(() => passageRuns(questions), [questions]);
  const sections = useMemo(
    () => (state.test && questions.length ? [{ testUuid: state.test.uuid, testName: state.test.name, questionIndices: questions.map((_, i) => i) }] : []),
    [state.test, questions]
  );
  const current = questions[index] || null;
  const run = runByIndex[index] || null;
  const passage = run && !isBlankHtml(state.passages[run.passageUuid]?.body) ? state.passages[run.passageUuid] : null;
  const runKey = run?.key || null;
  const calculatorAllowed = Boolean(state.test?.calculator_is_allowed);
  const calculatorShown = calculatorOpen && calculatorAllowed;

  // As in the exam: a new run starts at the top, a new question shows its top.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [runKey]);
  useEffect(() => {
    if (columnRef.current) columnRef.current.scrollTop = 0;
  }, [index]);

  // Left and right arrows change the question, as in the exam (not those pressed in the calculator or the table).
  useEffect(() => {
    if (!open || !questions.length) return undefined;
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, select, [contenteditable='true'], [data-exam-calculator], [data-exam-tool]")) return;
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, questions.length - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, questions.length]);

  const nav = current && (
    <QuestionNavPanel
      questions={questions}
      currentIndex={index}
      answeredMap={answers}
      testSections={sections}
      runByIndex={runByIndex}
      onNavigate={setIndex}
    />
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Panel className="fixed inset-0 flex flex-col bg-slate-50">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-blue-700">
                  <EyeIcon className="h-4 w-4" aria-hidden="true" />
                  {badge || t("test_bank.preview.badge")}
                </p>
                <Dialog.Title className="truncate text-base font-semibold text-slate-900">{state.test?.name || ""}</Dialog.Title>
                <p className="text-xs text-slate-500">{hint || t("test_bank.preview.hint")}</p>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {questions.length > 0 && (
                  <dl className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
                    <div className="flex items-baseline gap-1.5">
                      <dt className="text-slate-500">{t("test_bank.builder.stat_questions")}</dt>
                      <dd className="font-semibold tabular-nums text-slate-900">{totals.count}</dd>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <dt className="text-slate-500">{t("test_bank.editor.stat_points")}</dt>
                      <dd className="font-semibold tabular-nums text-emerald-700">{totals.points}</dd>
                    </div>
                    {DIFFICULTY_LEVELS.some((level) => totals.levels[level]) && (
                      <div className="flex items-center gap-1.5">
                        <dt className="sr-only">{t("difficulty.label")}</dt>
                        {DIFFICULTY_LEVELS.filter((level) => totals.levels[level]).map((level) => (
                          <dd key={level}>
                            <DifficultyBadge level={level} count={totals.levels[level]} />
                          </dd>
                        ))}
                      </div>
                    )}
                  </dl>
                )}
                {questions.length > 0 && (
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={showKey}
                      onChange={(e) => setShowKey(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                    />
                    {t("test_bank.preview.show_key")}
                  </label>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className={`inline-flex h-10 items-center gap-1.5 rounded-lg bg-white px-4 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 ${focusRing}`}
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  {t("buttons.close")}
                </button>
              </div>
            </header>
            {notice && (
              <p role="status" className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 sm:px-6">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                {notice}
              </p>
            )}

            {state.loading ? (
              <div className="flex flex-1 items-center justify-center">
                <Spinner className="h-8 w-8" />
              </div>
            ) : state.error ? (
              <div className="flex flex-1 items-center justify-center p-6">
                <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {getApiErrorMessage(t, state.error, "test_bank.preview.load_failed")}
                  <button type="button" onClick={() => setAttempt((n) => n + 1)} className={linkButtonClass}>
                    {t("test_bank.builder.retry")}
                  </button>
                </div>
              </div>
            ) : !current ? (
              <div className="flex flex-1 items-center justify-center p-6 text-sm text-slate-500">{t("test_bank.preview.empty")}</div>
            ) : (
              <div className="flex flex-1 overflow-hidden">
                <aside className="hidden w-64 flex-shrink-0 overflow-y-auto border-r border-slate-200 bg-white p-5 md:block">{nav}</aside>

                <main
                  ref={mainRef}
                  className={
                    passage
                      ? "flex-1 overflow-y-auto px-4 pb-24 pt-6 sm:px-6 lg:pt-10 xl:grid xl:grid-cols-[repeat(2,minmax(0,50rem))] xl:grid-rows-1 xl:justify-center xl:gap-6 xl:overflow-hidden xl:pb-6"
                      : "flex-1 overflow-y-auto px-4 pb-24 pt-6 sm:px-6 lg:pt-10"
                  }
                >
                  {passage && (
                    <PassagePane
                      key={runKey}
                      run={run}
                      passage={passage}
                      scrollPositions={scrollPositions.current}
                      className="mx-auto mb-4 max-w-3xl xl:mx-0 xl:mb-0 xl:max-w-none"
                    />
                  )}
                  <div
                    ref={columnRef}
                    className={
                      passage
                        ? `mx-auto max-w-3xl xl:mx-0 xl:min-h-0 xl:max-w-none xl:overflow-y-auto xl:px-1 ${calculatorShown ? "pb-[30rem]" : "xl:pb-24"}`
                        : "mx-auto max-w-3xl min-[1920px]:max-w-4xl"
                    }
                  >
                    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 md:hidden">{nav}</div>

                    {run && !passage && (
                      <div role="status" className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
                        {t("exam_taking.passage.unavailable")}
                      </div>
                    )}

                    <p className="mb-4 text-sm font-medium text-slate-500">
                      {t("exam_taking.question")} <span className="font-semibold tabular-nums text-slate-900">{index + 1}</span>
                      <span className="tabular-nums"> / {questions.length}</span>
                    </p>

                    {!isBlankHtml(state.test?.additional_information) && (
                      <details className="group mb-4 overflow-hidden rounded-xl border border-blue-100 bg-blue-50/60">
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 transition-colors duration-200 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
                          <span className="flex items-center gap-2 text-sm font-medium text-blue-900">
                            <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden="true" />
                            {t("exam_taking.additional_information")}
                          </span>
                          <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-blue-700 transition-transform duration-200 group-open:rotate-180" aria-hidden="true" />
                        </summary>
                        <div className="max-w-none border-t border-blue-100 bg-white px-4 py-4 text-sm leading-6 text-slate-700">
                          <KaTeXComponent text={state.test.additional_information} />
                        </div>
                      </details>
                    )}

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
                      <QuestionDisplay
                        question={current}
                        selectedAnswerUuid={answers[current.attempt_question_uuid]}
                        onSelectAnswer={(questionUuid, optionUuid) => setAnswers((prev) => ({ ...prev, [questionUuid]: optionUuid }))}
                        isReview={showKey}
                        correctAnswerUuids={showKey ? current.correct : []}
                        difficulty={current.difficulty}
                        answersHiddenNote={t("test_bank.builder.answers_hidden_note")}
                      />
                    </div>

                    <div className="mt-6 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setIndex((i) => Math.max(i - 1, 0))}
                        disabled={index === 0}
                        className={`${navButtonClass} bg-white text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50`}
                      >
                        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                        {t("buttons.back")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIndex((i) => Math.min(i + 1, questions.length - 1))}
                        disabled={index === questions.length - 1}
                        className={`${navButtonClass} bg-blue-600 text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700`}
                      >
                        {t("buttons.next")}
                        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </main>
              </div>
            )}

            {current && calculatorAllowed && <Calculator onOpenChange={setCalculatorOpen} />}
            {current && state.test?.periodic_table_is_allowed && (
              <PeriodicTable calculatorAllowed={calculatorAllowed} calculatorOpen={calculatorShown} />
            )}
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
